/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import {
  addDays,
  addYears,
  fromYMDOrUndefined,
  getDay,
  getMonth,
  getYear,
  isLeapDay,
  isLeapYear,
  maxDayDate,
  maxDayDateYear
} from '../day-date';
import DayInterval from '../day-interval/day-interval';

/**
 * @private
 * @typedef {import('../day-date').DayDate} DayDate
 */

/**
 * Souvislý interval prázdninového plánu s granularitou dnů zadaný počátečním
 * a koncovým dnem (včetně) v lokální časové zóně. Koncový den musí být stejný
 * nebo větší jako počáteční, takže minimální délka intervalu je 1 den (a tedy
 * interval o délce 0 dní nelze reprezentovat).
 *
 * Konstruktor a statické konstrukční funkce vždy kontrolují validitu
 * start/end datumů a zda je end >= start, případně vyhodí TypeError.
 *
 * Sémantika případného (pouze) ročního opakování obecně říká,
 * že opakované instance je od prvního výskytu do praktického nekonečna, tj.
 * dokud nepřeteče typ {@link DayDate}, kdy se opakování ukončí bez výjimky.
 * Jak se vybírá start/end den instance opakování určují až dané implementace
 * generátorů {@link nonIcalGenerator} a {@link icalGenerator}.
 *
 * Tato třída ctí immutable přístup a jakákoliv modifikace vrací nový objekt.
 */
export default class HolidayInterval extends DayInterval {
  /**
   * @protected
   * @type {typeof HolidayInterval['fromObject']}
   * @throws {TypeError}
   */
  constructor(o) {
    super(o);

    /**
     * Opakování každý rok.
     * @readonly
     */
    this.repeatYearly = o.repeatYearly ?? false;

    if (new.target === HolidayInterval) {
      Object.freeze(this);
    }
  }

  toString() {
    return `HolidayInterval(${this.start}, ${this.end}${
      this.repeatYearly ? ', true' : ''
    }) days=${this.days}`;
  }

  /**
   * Vytvoří nový prázdninový interval.
   * @param {DayDate} start Počáteční den intervalu.
   * @param {DayDate} end Koncový den intervalu (včetně).
   * @param {boolean} [repeatYearly = false] Opakování každý rok.
   * @returns {HolidayInterval}
   * @throws {TypeError}
   */
  static from(start, end, repeatYearly) {
    return new HolidayInterval({ start, end, repeatYearly });
  }

  /**
   * Vytvoří nový prázdninový interval z objektu.
   * @param {Object} o
   * @param {DayDate} o.start Počáteční den intervalu.
   * @param {DayDate} o.end Koncový den intervalu (včetně).
   * @param {boolean} [o.repeatYearly = false] Opakování každý rok.
   * @returns {HolidayInterval}
   * @throws {TypeError}
   */
  static fromObject(o) {
    return new HolidayInterval(o);
  }

  /**
   * @param {boolean} repeatYearly
   * @returns {this}
   */
  setRepeatYearly(repeatYearly) {
    return new /** @type {import('../day-interval/day-interval').DayIntervalConstructor<this>} */ (
      this.constructor
    )({
      ...this,
      repeatYearly
    });
  }

  /**
   * Vrátí generátor intervalů dnů.
   *
   * Pro neopakované intervaly vrátí generátor s přesně jedním prvkem.
   *
   * Pro ročně opakované intervaly vrátí potenciálně nekonečný generátor
   * vracející neklesající sekvenci intervalů, které se mohou obecně překrývat
   * (např. pro interval delší než rok). Poslední interval jdoucí přes
   * konec věků bude saturován a iterace ukončena.
   * @returns {Generator<DayInterval>}
   */
  *nonIcalGenerator() {
    if (!this.repeatYearly) {
      yield DayInterval.fromObject(this);
      return;
    }

    // Uložíme si, zda původní interval má začátek anebo konec na přestupný den
    // v roce. Pro nepřestupné roky platí, že přestupný den 29.2. se "zaokrouhlí"
    // na 28.2.
    const isLeapStart = isLeapDay(this.start);
    const isLeapEnd = isLeapDay(this.end);
    const isLeapStartOrEnd = isLeapStart || isLeapEnd;
    let next = DayInterval.fromObject(this);
    while (true) {
      yield next;

      // Kontrola, zda by další opakování intervalu bylo za koncem věků.
      if (getYear(next.start) === maxDayDateYear) {
        return;
      }

      next = DayInterval.from(
        addYears(next.start, 1),
        // Případná saturace konce po konci věků právě na konec věků.
        getYear(next.end) === maxDayDateYear
          ? maxDayDate
          : addYears(next.end, 1)
      );

      // Jen pokud je začátek nebo konec intervalu na přestupném dni,
      // tak je potřeba provést případnou korekci přičtením jednoho dne,
      // abychom se opět dostali na 29.2. daného roku.
      if (isLeapStartOrEnd) {
        // Abychom nevyrobili průběžně nevalidní interval (výjimka),
        // tak je potřeba nejprve nastavit konec a pak začátek.
        if (isLeapEnd && isLeapYear(next.end)) {
          next = next.setEnd(addDays(next.end, 1));
        }
        if (isLeapStart && isLeapYear(next.start)) {
          next = next.setStart(addDays(next.start, 1));
        }
      }
    }
  }

  /**
   * Vrátí generátor intervalů dnů podle iCAL sémantiky, zjednodušeně:
   * - řeší se existence opakování startu původního intervalu, tj. start
   *   na přestupném dni se opakuje co 4/8 let (ne/přestupné století)
   * - konce dalších iterací intervalu se určují podle startu dané iterace
   *   a počtu dní původního intervalu
   *
   * Pro neopakované intervaly vrátí generátor s přesně jedním prvkem.
   *
   * Pro ročně opakované intervaly vrátí potenciálně nekonečný generátor
   * vracející neklesající sekvenci intervalů, které se mohou obecně překrývat
   * (např. pro interval delší než rok). Poslední interval jdoucí přes
   * konec věků bude saturován a iterace ukončena.
   * @returns {Generator<DayInterval>}
   */
  // eslint-disable-next-line max-statements
  *icalGenerator() {
    if (!this.repeatYearly) {
      yield DayInterval.fromObject(this);
      return;
    }

    let year = getYear(this.start);
    const month = getMonth(this.start);
    const day = getDay(this.start);
    const days = this.days;
    // Podle toho, zda má původní interval začátek na přestupném dni,
    // inkrementujeme ve smyčce daný počet let - je to jen optimalizace
    // pro přestupné roky, algoritmus by fungoval i bez toho.
    const yearIncrement = isLeapDay(this.start) ? 4 : 1;

    let next = DayInterval.fromObject({ start: this.start, end: this.end });
    while (true) {
      yield next;

      /** @type {DayDate | undefined} */
      let start;
      do {
        year += yearIncrement;
        if (year > maxDayDateYear) {
          return;
        }
        // Zde používáme variantu fromYMDOrUndefined, která nevyhazuje výjimku,
        // jelikož potřebuje nevyhodit při šáhnutí na 29.2. nepřestupného roku
        // (to se může stát při addYear === 4 tehdy, pokud se jedná
        // o (ne)přestupné století, kdy může být mezera 8 let a nikoliv jen 4).
        start = fromYMDOrUndefined(year, month, day);
      } while (!start);

      /** @type {DayDate} */
      let end;
      try {
        end = addDays(start, days - 1);
      } catch {
        // Pokud výpočet end vrhne, tak musí být za koncem věků, proto
        // saturujeme na konec věků.
        end = maxDayDate;
      }

      next = DayInterval.from(start, end);
    }
  }
}
